// SUB-OFFICE 7 — the Ceremony Director. A generic timeline/cue system for
// everything staged: camera moves (via SceneManager's cue stack), light lerps
// (global dim + post uniforms), entity cues, and chyron sync. Ceremonies are
// data — arrays of cues — so the adjudication phase table and the scripted
// arcs read like the stage directions they are.
//
// Two cue species, honoring the motion law:
//   at(t, fn)            — MECHANISM: fires once, all at once, no easing
//   lerp(t0, t1, fn(k))  — BODY/LIGHT: eased 0..1 over the window
//   hold(t, until)       — the timeline refuses to pass t until the condition
//                          resolves (latency lives here, masked, unhurried)

export interface TimelineCue {
  t: number;
  fn?: () => void;
  lerpEnd?: number;
  lerpFn?: (k: number) => void;
  holdUntil?: () => boolean;
}

export class Timeline {
  private cues: TimelineCue[] = [];
  private fired = new Set<TimelineCue>();
  private time = 0;
  private duration = 0;

  at(t: number, fn: () => void): this {
    this.cues.push({ t, fn });
    this.duration = Math.max(this.duration, t);
    return this;
  }

  lerp(t0: number, t1: number, fn: (k: number) => void): this {
    this.cues.push({ t: t0, lerpEnd: t1, lerpFn: fn });
    this.duration = Math.max(this.duration, t1);
    return this;
  }

  /** The timeline waits at `t` until the condition is true. */
  hold(t: number, until: () => boolean): this {
    this.cues.push({ t, holdUntil: until });
    this.duration = Math.max(this.duration, t);
    return this;
  }

  /** Advance; returns true while running, false when complete. */
  update(dt: number): boolean {
    // holds gate the clock itself
    let gate = Infinity;
    for (const c of this.cues) {
      if (c.holdUntil && !this.fired.has(c)) {
        if (this.time >= c.t) {
          if (c.holdUntil()) this.fired.add(c);
          else gate = Math.min(gate, c.t);
        } else {
          gate = Math.min(gate, c.t);
        }
      }
    }
    this.time = Math.min(this.time + dt, gate === Infinity ? this.time + dt : gate);

    for (const c of this.cues) {
      if (c.holdUntil) continue;
      if (c.lerpFn && c.lerpEnd !== undefined) {
        if (this.time >= c.t && !this.fired.has(c)) {
          const k = Math.min(1, (this.time - c.t) / Math.max(0.0001, c.lerpEnd - c.t));
          c.lerpFn(k);
          if (k >= 1) this.fired.add(c);
        }
      } else if (c.fn && this.time >= c.t && !this.fired.has(c)) {
        this.fired.add(c);
        c.fn();
      }
    }
    // complete when past duration and every hold has resolved
    if (this.time < this.duration) return true;
    for (const c of this.cues) {
      if (c.holdUntil && !this.fired.has(c)) return true;
      if (c.lerpFn && !this.fired.has(c)) return true;
    }
    return false;
  }

  get elapsed(): number {
    return this.time;
  }
}

/** Owns at most one running timeline; updated from the main loop. */
export class CeremonyDirector {
  private current: Timeline | null = null;
  private resolve: (() => void) | null = null;

  get busy(): boolean {
    return this.current !== null;
  }

  /** Play a timeline to completion. Resolves when the last cue lands. */
  play(tl: Timeline): Promise<void> {
    // a new ceremony displaces the old one (the Bureau does not overlap rites)
    if (this.resolve) this.resolve();
    this.current = tl;
    return new Promise((res) => {
      this.resolve = res;
    });
  }

  stop(): void {
    if (this.resolve) this.resolve();
    this.current = null;
    this.resolve = null;
  }

  update(dt: number): void {
    if (!this.current) return;
    if (!this.current.update(dt)) {
      const res = this.resolve;
      this.current = null;
      this.resolve = null;
      if (res) res();
    }
  }
}

/** Small await-able pause for imperative ceremony code. */
export function delay(ms: number): Promise<void> {
  return new Promise((res) => window.setTimeout(res, ms));
}
